/**
 * Owner-centric self-service delegation (D12) — the non-grantor, non-admin
 * twin of GrantAssignmentDialog's designee branch. That dialog mints a grant
 * directly (grantor/admin only); this one FILES an access_request with
 * intent='delegation' instead — the signed-in user is always the owner, a
 * reviewer must approve before anything mints (see
 * services/enies-lobby/pkg/connecthandlers/access_requests.go).
 */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Timestamp } from '@bufbuild/protobuf';
import { ConnectError } from '@connectrpc/connect';
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label, Textarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useAuth } from '../../../contexts/authContext';
import { classifyRequestAccessOutcome, useListScopeNodes, useRequestAccess, useSearchTenantMembers } from './hooks';
// Local calendar date (not UTC) — mirrors GrantAssignmentDialog's
// todayDateStr so "today" reads the same way on both delegation surfaces.
function todayDateStr() {
    const d = new Date();
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${d.getFullYear()}-${month}-${day}`;
}
// Local calendar date, human-readable — same non-UTC interpretation as
// todayDateStr, just formatted for display instead of a date input's value.
// toISOString() would coerce to UTC and can print the wrong calendar day.
function formatLocalDate(d) {
    return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
}
export function DelegateAccessRequestDialog({ open, onOpenChange, onCreated }) {
    const { user } = useAuth();
    const [delegateUserId, setDelegateUserId] = useState('');
    const [delegateName, setDelegateName] = useState('');
    const [delegateQuery, setDelegateQuery] = useState('');
    const [scopeQuery, setScopeQuery] = useState('');
    const [scopeNodeId, setScopeNodeId] = useState('');
    const [scopeTarget, setScopeTarget] = useState(null);
    const [effectiveStart, setEffectiveStart] = useState(todayDateStr);
    const [effectiveEnd, setEffectiveEnd] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [alreadyPending, setAlreadyPending] = useState(false);
    const [decidedStatus, setDecidedStatus] = useState('');
    // Set only when the filing SUCCEEDED past a prior denial of the identical
    // window (000114) - informational, never an error. Distinct from
    // alreadyPending/decidedStatus, which both mean nothing new was filed.
    const [priorDenialAt, setPriorDenialAt] = useState(null);
    const delegateMembers = useSearchTenantMembers(delegateQuery, {
        enabled: open
    });
    const scopeNodes = useListScopeNodes(scopeQuery, {
        enabled: open
    });
    const requestAccess = useRequestAccess();
    const startAfterEnd = !!effectiveStart && !!effectiveEnd && effectiveStart > effectiveEnd;
    const invalid = !delegateUserId || !scopeTarget || !effectiveStart || !effectiveEnd || startAfterEnd;
    const reset = ()=>{
        setDelegateUserId('');
        setDelegateName('');
        setDelegateQuery('');
        setScopeQuery('');
        setScopeNodeId('');
        setScopeTarget(null);
        setEffectiveStart(todayDateStr());
        setEffectiveEnd('');
        setMessage('');
        setError('');
        setAlreadyPending(false);
        setDecidedStatus('');
        setPriorDenialAt(null);
    };
    const submit = async ()=>{
        if (invalid || !user?.id || !scopeTarget) return;
        setError('');
        setAlreadyPending(false);
        setDecidedStatus('');
        setPriorDenialAt(null);
        let outcome;
        try {
            const resp = await requestAccess.mutateAsync({
                targetObjectType: scopeTarget.type,
                targetObjectId: scopeTarget.id,
                intent: 'delegation',
                message,
                originalOwnerUserId: user.id,
                delegateUserId,
                // Start of day, local — a delegation dated today must cover all of
                // today, not "whenever this form happened to submit".
                effectiveStart: Timestamp.fromDate(new Date(`${effectiveStart}T00:00:00`)),
                // End of day, local — a delegation runs through its last day.
                effectiveEnd: Timestamp.fromDate(new Date(`${effectiveEnd}T23:59:59`))
            });
            outcome = classifyRequestAccessOutcome(resp);
        } catch (err) {
            // Surfaced inline (not just the hook's toast) so the reason a
            // delegation request was rejected — e.g. the window exceeds the
            // tenant's designee_max_duration_days, or the approved-prior block
            // (000114) — is visible next to the field that caused it, not
            // swallowed. ConnectError.message is prefixed with "[code] "
            // (connect-error.js's createMessage); rawMessage is the server's
            // text verbatim, which is what the user needs to read.
            if (err instanceof ConnectError) {
                setError(err.rawMessage);
            } else {
                setError(err instanceof Error ? err.message : 'Failed to file delegation request');
            }
            return;
        }
        if (outcome.kind === 'pending') {
            // Converged on the existing pending row (D12) - nothing new was filed,
            // so the dialog must stay open and say so instead of closing as if it
            // had succeeded.
            setAlreadyPending(true);
            return;
        }
        if (outcome.kind === 'decided') {
            // The idempotency-key lookup is now status-scoped to pending, so a
            // decided row can no longer come back from a fresh filing - this
            // branch is defensive, reachable only if a reply comes from a
            // replica that predates that fix (a mixed-version rollout window).
            // Kept so the UI never misreports a decided request as "already
            // pending" if that window is ever hit.
            setDecidedStatus(outcome.status);
            return;
        }
        if (outcome.priorDenialAt) {
            // The filing SUCCEEDED — unlike alreadyPending/decidedStatus above,
            // this is not a no-op. Stay open just long enough for the user to see
            // the notice; onCreated (list refresh) fires when they dismiss it,
            // not now, since it's the same event either way.
            setPriorDenialAt(outcome.priorDenialAt.toDate());
            return;
        }
        reset();
        onOpenChange(false);
        onCreated?.();
    };
    return /*#__PURE__*/ _jsx(Dialog, {
        open: open,
        onOpenChange: (o)=>{
            if (!o) reset();
            onOpenChange(o);
        },
        children: /*#__PURE__*/ _jsxs(DialogContent, {
            "data-testid": Testids.adminAccess.delegateRequestDialog,
            children: [
                /*#__PURE__*/ _jsxs(DialogHeader, {
                    children: [
                        /*#__PURE__*/ _jsx(DialogTitle, {
                            children: "Delegate my access"
                        }),
                        /*#__PURE__*/ _jsx(DialogDescription, {
                            children: "Files a request for a reviewer to approve — nothing is granted until then. The delegate receives a time-boxed mirror of the access you hold today at this scope."
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4 py-2",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "delegate-request-user-search",
                                    children: "Delegate to"
                                }),
                                /*#__PURE__*/ _jsx(Input, {
                                    id: "delegate-request-user-search",
                                    "data-testid": Testids.adminAccess.delegateRequestUserInput,
                                    value: delegateQuery,
                                    onChange: (e)=>setDelegateQuery(e.target.value),
                                    placeholder: "Search people by name"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "max-h-40 overflow-y-auto rounded-md border",
                                    children: [
                                        delegateQuery.trim().length < 2 && /*#__PURE__*/ _jsx("p", {
                                            className: "text-muted-foreground p-2 text-xs",
                                            children: "Type a name to search…"
                                        }),
                                        delegateQuery.trim().length >= 2 && delegateMembers.isLoading && /*#__PURE__*/ _jsx("p", {
                                            className: "text-muted-foreground p-2 text-xs",
                                            children: "Searching…"
                                        }),
                                        delegateQuery.trim().length >= 2 && delegateMembers.data && delegateMembers.data.length === 0 && /*#__PURE__*/ _jsx("p", {
                                            className: "text-muted-foreground p-2 text-xs",
                                            children: "No matching people."
                                        }),
                                        delegateQuery.trim().length >= 2 && (delegateMembers.data ?? []).map((m)=>/*#__PURE__*/ _jsx("button", {
                                                type: "button",
                                                "data-testid": Testids.adminAccess.delegateRequestUserOption,
                                                "data-user-id": m.id,
                                                onClick: ()=>{
                                                    setDelegateUserId(m.id);
                                                    setDelegateName(m.displayName);
                                                },
                                                className: `block w-full px-2 py-1.5 text-left text-xs hover:bg-accent ${delegateUserId === m.id ? 'bg-accent font-medium' : ''}`,
                                                children: m.displayName
                                            }, m.id))
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-muted-foreground text-xs",
                                    "data-testid": Testids.adminAccess.delegateRequestUserSelected,
                                    "data-delegate-user-id": delegateUserId,
                                    children: delegateName ? `Selected: ${delegateName}` : 'No delegate selected yet'
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "delegate-request-scope-search",
                                    children: "Scope"
                                }),
                                /*#__PURE__*/ _jsx(Input, {
                                    id: "delegate-request-scope-search",
                                    "data-testid": Testids.adminAccess.delegateRequestScopeSearchInput,
                                    value: scopeQuery,
                                    onChange: (e)=>setScopeQuery(e.target.value),
                                    placeholder: "Search scope nodes by name or target id"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "max-h-40 overflow-y-auto rounded-md border",
                                    children: [
                                        scopeNodes.isLoading && /*#__PURE__*/ _jsx("p", {
                                            className: "text-muted-foreground p-2 text-xs",
                                            children: "Loading scope nodes…"
                                        }),
                                        scopeNodes.data && scopeNodes.data.length === 0 && /*#__PURE__*/ _jsx("p", {
                                            className: "text-muted-foreground p-2 text-xs",
                                            children: "No matching scope nodes."
                                        }),
                                        (scopeNodes.data ?? []).map((n)=>/*#__PURE__*/ _jsxs("button", {
                                                type: "button",
                                                "data-testid": Testids.adminAccess.delegateRequestScopeOption,
                                                "data-scope-node-id": n.id,
                                                onClick: ()=>{
                                                    setScopeNodeId(n.id);
                                                    setScopeTarget({
                                                        type: n.targetObjectType,
                                                        id: n.targetObjectId
                                                    });
                                                },
                                                className: `block w-full px-2 py-1.5 text-left text-xs hover:bg-accent ${scopeNodeId === n.id ? 'bg-accent font-medium' : ''}`,
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        className: "font-medium",
                                                        children: n.displayName || n.targetObjectId
                                                    }),
                                                    ' ',
                                                    /*#__PURE__*/ _jsxs("span", {
                                                        className: "text-muted-foreground",
                                                        children: [
                                                            n.kind,
                                                            " · ",
                                                            n.targetObjectType,
                                                            " · ",
                                                            n.targetObjectId
                                                        ]
                                                    })
                                                ]
                                            }, n.id))
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Input, {
                                    id: "delegate-request-scope",
                                    "data-testid": Testids.adminAccess.delegateRequestScopeInput,
                                    value: scopeNodeId,
                                    readOnly: true,
                                    placeholder: "Selected scope node"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "delegate-request-start",
                                    children: "Starts on"
                                }),
                                /*#__PURE__*/ _jsx(Input, {
                                    id: "delegate-request-start",
                                    type: "date",
                                    "data-testid": Testids.adminAccess.delegateRequestStartDateInput,
                                    value: effectiveStart,
                                    onChange: (e)=>setEffectiveStart(e.target.value)
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "delegate-request-end",
                                    children: "Ends on"
                                }),
                                /*#__PURE__*/ _jsx(Input, {
                                    id: "delegate-request-end",
                                    type: "date",
                                    "data-testid": Testids.adminAccess.delegateRequestEndDateInput,
                                    value: effectiveEnd,
                                    onChange: (e)=>setEffectiveEnd(e.target.value)
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-[11px] text-muted-foreground",
                                    children: "Required — delegation is always time-boxed (your organization caps the window)."
                                }),
                                startAfterEnd && /*#__PURE__*/ _jsx("p", {
                                    className: "text-destructive text-xs",
                                    children: "Start date must be on or before the end date."
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "delegate-request-reason",
                                    children: "Why? (optional)"
                                }),
                                /*#__PURE__*/ _jsx(Textarea, {
                                    id: "delegate-request-reason",
                                    "data-testid": Testids.adminAccess.delegateRequestReasonInput,
                                    value: message,
                                    onChange: (e)=>setMessage(e.target.value),
                                    placeholder: "A sentence helps the reviewer say yes faster",
                                    rows: 3
                                })
                            ]
                        }),
                        error && /*#__PURE__*/ _jsx("p", {
                            className: "text-destructive text-sm",
                            "data-testid": Testids.adminAccess.delegateRequestError,
                            children: error
                        }),
                        alreadyPending && /*#__PURE__*/ _jsx("p", {
                            className: "text-muted-foreground text-sm",
                            "data-testid": Testids.adminAccess.delegateRequestAlreadyPending,
                            children: "You already have a pending request for this owner, delegate, and scope."
                        }),
                        decidedStatus && /*#__PURE__*/ _jsxs("p", {
                            className: "text-muted-foreground text-sm",
                            "data-testid": Testids.adminAccess.delegateRequestDecided,
                            children: [
                                "Your last request for this owner, delegate, and scope was already ",
                                decidedStatus,
                                " - this one was not filed again."
                            ]
                        }),
                        priorDenialAt && /*#__PURE__*/ _jsxs("p", {
                            className: "rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800",
                            "data-testid": Testids.adminAccess.delegateRequestPriorDenial,
                            children: [
                                "Request filed. An identical request was denied on ",
                                formatLocalDate(priorDenialAt),
                                "."
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(DialogFooter, {
                    children: priorDenialAt ? /*#__PURE__*/ _jsx(Button, {
                        "data-testid": Testids.adminAccess.delegateRequestSubmitButton,
                        onClick: ()=>{
                            reset();
                            onOpenChange(false);
                            onCreated?.();
                        },
                        children: "Done"
                    }) : /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                variant: "outline",
                                onClick: ()=>onOpenChange(false),
                                children: "Cancel"
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                "data-testid": Testids.adminAccess.delegateRequestSubmitButton,
                                onClick: submit,
                                disabled: requestAccess.isPending || invalid,
                                children: requestAccess.isPending ? 'Requesting…' : 'Request delegation'
                            })
                        ]
                    })
                })
            ]
        })
    });
}
